import styled, { keyframes, css } from 'styled-components';
import { FaSpinner } from 'react-icons/fa';


const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(29, 134, 255, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(29, 134, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(29, 134, 255, 0);
  }
`;

export const Container = styled.div`
    display: flex;
    height: 100vh;
    width: 100vw;
    justify-content: center;
    align-items: center;
    background: linear-gradient(135deg, #0b1a30 0%, #004d7e 100%);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    overflow: hidden;
    padding: 20px;
    box-sizing: border-box;
`;

export const TutorialCard = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 90vh;
    max-width: 800px;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 24px;
    padding: 2.5rem;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.4);
    animation: ${fadeIn} 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    position: relative;
    overflow: hidden;

    h2 {
        margin-top: 0.5rem;
        margin-bottom: 0.5rem;
        color: #0f172a;
        text-align: center;
        font-size: 1.85rem;
        font-weight: 800;
        letter-spacing: -0.5px;
    }

    .description {
        margin: 0 0 1.5rem 0;
        font-size: 1.05rem;
        color: #475569;
        text-align: center;
        line-height: 1.6;
    }
`;

export const ProgressBar = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 6px;
    background-color: #e2e8f0;
    display: flex;
`;

export const ProgressFill = styled.div`
    height: 100%;
    width: ${props => props.progress}%;
    background: linear-gradient(90deg, #1d86ff 0%, #00d4ff 100%);
    transition: width 0.4s cubic-bezier(0.16, 1, 0.3, 1);
`;

export const HeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    position: relative;
`;

export const StepBadge = styled.span`
    background-color: rgba(29, 134, 255, 0.1);
    color: #1d86ff;
    padding: 6px 14px;
    border-radius: 99px;
    font-size: 0.85rem;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
`;

export const ImmersiveButton = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    background: linear-gradient(135deg, #1d86ff 0%, #006aff 100%);
    color: #fff;
    border: none;
    padding: 8px 16px;
    border-radius: 99px;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(29, 134, 255, 0.35);
    transition: all 0.3s ease;
    animation: ${pulse} 2s infinite;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(29, 134, 255, 0.5);
    }

    &:active {
        transform: translateY(0);
    }
`;

export const MediaContainer = styled.div`
    position: relative;
    margin-bottom: 1.8rem;
    width: 100%;
    height: 100%;
    background-color: #0f172a;
    border-radius: 16px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    overflow: hidden;
    box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.25);
    transition: all 0.3s ease;

    /* Força o componente de mídia (primeiro filho div) a preencher 100% do container */
    > div {
        width: 100%;
        height: 100%;
    }

    /* Estilos quando o próprio MediaContainer entra em Fullscreen */
    &:fullscreen {
        width: 100vw;
        height: 100vh;
        max-width: none;
        max-height: none;
        border-radius: 0;
        background-color: #090d16;
        margin: 0;
        border: none;
    }

    &:-webkit-full-screen {
        width: 100vw;
        height: 100vh;
        max-width: none;
        max-height: none;
        border-radius: 0;
        background-color: #090d16;
        margin: 0;
        border: none;
    }
`;

export const StepIndicator = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
`;

export const StepDot = styled.div`
    width: ${props => props.active ? '20px' : '8px'};
    height: 8px;
    border-radius: 4px;
    background-color: ${props => props.active ? '#1D86FF' : '#cbd5e1'};
    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
`;

export const Actions = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: auto;
    gap: 16px;

    button {
        outline: none;
        border: none;
        height: 3rem;
        padding: 0 2rem;
        border-radius: 12px;
        cursor: pointer;
        font-size: 1rem;
        font-weight: 600;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
    }

    .btn-prev {
        background: #f1f5f9;
        color: #475569;
        border: 1px solid #e2e8f0;

        &:hover {
            background: #e2e8f0;
            color: #0f172a;
        }
    }

    .btn-next {
        background: linear-gradient(135deg, #1d86ff 0%, #0056d6 100%);
        color: #fff;
        box-shadow: 0 4px 12px rgba(29, 134, 255, 0.25);
        margin-left: auto;

        &:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 6px 16px rgba(29, 134, 255, 0.35);
        }
        
        &:active:not(:disabled) {
            transform: translateY(0);
        }

        &:disabled {
            background: #cbd5e1;
            color: #64748b;
            box-shadow: none;
            cursor: not-allowed;
        }
    }
`;

export const LoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #cbd5e1;
    font-size: 1.25rem;
    font-weight: 600;
    gap: 16px;
    
    svg {
        animation: spin 1s linear infinite;
    }

    @keyframes spin {
        100% { transform: rotate(360deg); }
    }
`;

/* Fullscreen Nav Controls */
export const FullscreenNav = styled.button`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(15, 23, 42, 0.6);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.15);
    color: #fff;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 1000;
    transition: all 0.3s ease;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.4);

    &:hover {
        background: rgba(29, 134, 255, 0.85);
        border-color: #1d86ff;
        transform: translateY(-50%) scale(1.1);
    }

    &:active {
        transform: translateY(-50%) scale(0.95);
    }

    ${props => props.left ? css`
        left: 24px;
    ` : css`
        right: 24px;
    `}
`;

export const FullscreenCloseBtn = styled.button`
    position: absolute;
    top: 24px;
    left: 24px;
    background: rgba(15, 23, 42, 0.6);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.15);
    color: #fff;
    padding: 8px 16px;
    border-radius: 99px;
    font-size: 0.85rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    z-index: 1000;
    transition: all 0.3s ease;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.4);

    &:hover {
        background: #ef4444;
        border-color: #ef4444;
        transform: scale(1.05);
    }
`;

export const FullscreenStepIndicator = styled.div`
    position: absolute;
    top: 24px;
    right: 24px;
    background: rgba(15, 23, 42, 0.6);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.15);
    color: #fff;
    padding: 6px 16px;
    border-radius: 99px;
    font-size: 0.9rem;
    font-weight: 700;
    z-index: 1000;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.4);
`;

export const ButtonSpinner = styled(FaSpinner)`
    animation: spin 1s linear infinite;
`;
